/* Post-login app selector — full-screen workspace chooser.

   Shown immediately after authentication when the user has not yet
   picked a preferred workspace (localStorage key `vault_preferred_app`).
   The two cards (THE VAULT = indigo, THE TRADING VAULT = green) feel
   like two distinct products under one brand umbrella rather than
   "an app with a hidden trading tab". */

import { useState } from "react";

const C = {
  pageBg:     "#0f172a",
  cardBg:     "#131316",
  cardBorder: "#26262A",
  cardText:   "#F4F4F5",
  cardMuted:  "#71717A",
  indigo:     "#4f46e5",
  indigoHov:  "#4338ca",
  green:      "#10b981",
  greenHov:   "#059669",
  subtitle:   "#64748b",
  link:       "#94a3b8",
};

const SYSTEM_FONT =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export default function AppSelector({ onSelectVault, onSelectTrading, onLogout }) {
  const [remember, setRemember] = useState(false);
  const [hoverVault, setHoverVault]     = useState(false);
  const [hoverTrading, setHoverTrading] = useState(false);
  const [hoverBtnVault, setHoverBtnVault]     = useState(false);
  const [hoverBtnTrading, setHoverBtnTrading] = useState(false);

  const handleSelect = (app) => {
    if (remember) {
      try { localStorage.setItem("vault_preferred_app", app); } catch { /* noop */ }
    }
    if (app === "vault") onSelectVault();
    else onSelectTrading();
  };

  return (
    <div
      data-testid="app-selector"
      style={{
        minHeight: "100vh",
        background: C.pageBg,
        fontFamily: SYSTEM_FONT,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "64px 16px 32px",
        boxSizing: "border-box",
      }}
    >
      {/* Inline keyframes for the LIVE-dot pulse. Scoped to this component. */}
      <style>{`
        @keyframes vault_app_selector_pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
        @media (max-width: 767px) {
          .vault-selector-cards { flex-direction: column !important; align-items: stretch !important; }
          .vault-selector-card  { width: 100% !important; max-width: 100% !important; }
        }
      `}</style>

      {/* ── Top bar ───────────────────────────────────────── */}
      <div style={{ textAlign: "center", marginBottom: 56 }} data-testid="selector-header">
        <div
          style={{
            color: "#fff",
            fontFamily: SYSTEM_FONT,
            fontSize: 13,
            fontWeight: 800,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
          }}
        >
          THE VAULT
        </div>
        <div
          style={{
            color: C.subtitle,
            fontSize: 11,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            marginTop: 6,
          }}
        >
          Choose your workspace
        </div>
      </div>

      {/* ── Two cards side-by-side (stacks on mobile) ────── */}
      <div
        className="vault-selector-cards"
        style={{
          display: "flex",
          gap: 24,
          justifyContent: "center",
          alignItems: "stretch",
          width: "100%",
          maxWidth: 720,
        }}
      >
        {/* ── Card 1: THE VAULT (Budget & Debt) ────────────── */}
        <div
          className="vault-selector-card"
          data-testid="selector-card-vault"
          onClick={() => handleSelect("vault")}
          onMouseEnter={() => setHoverVault(true)}
          onMouseLeave={() => setHoverVault(false)}
          style={{
            background: C.cardBg,
            border: `1px solid ${hoverVault ? C.indigo : C.cardBorder}`,
            borderRadius: 12,
            cursor: "pointer",
            transition: "border-color 0.2s, transform 0.2s",
            transform: hoverVault ? "translateY(-4px)" : "translateY(0)",
            padding: "32px 28px",
            width: 320,
            minHeight: 380,
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          {/* Icon */}
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 10,
              background: "rgba(79,70,229,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 2v10l6.5 6.5" stroke={C.indigo} strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="12" r="10" stroke={C.indigo} strokeWidth="2" />
            </svg>
          </div>

          {/* App name */}
          <div
            style={{
              marginTop: 20,
              color: C.cardText,
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: "-0.02em",
            }}
          >
            THE VAULT
          </div>
          <div style={{ color: C.cardMuted, fontSize: 13, marginTop: 4 }}>
            Budget &amp; Debt Tracker
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: C.cardBorder, margin: "20px 0" }} />

          {/* Feature list */}
          <ul style={{ listStyle: "none", margin: 0, padding: 0, color: C.cardMuted, fontSize: 12, lineHeight: 1.8 }}>
            {[
              "Debt payoff tracker & snowball planner",
              "Monthly budget vs actual spending",
              "Bank connection via TrueLayer",
            ].map((f) => (
              <li key={f} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ color: C.indigo, fontWeight: 700 }}>•</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>

          {/* Bottom button */}
          <button
            data-testid="selector-btn-vault"
            onClick={(e) => { e.stopPropagation(); handleSelect("vault"); }}
            onMouseEnter={() => setHoverBtnVault(true)}
            onMouseLeave={() => setHoverBtnVault(false)}
            style={{
              marginTop: "auto",
              background: hoverBtnVault ? C.indigoHov : C.indigo,
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "12px 20px",
              fontSize: 13,
              fontWeight: 600,
              width: "100%",
              cursor: "pointer",
              transition: "background 0.15s",
            }}
          >
            Open The Vault →
          </button>
        </div>

        {/* ── Card 2: THE TRADING VAULT (Pro Trading Suite) ── */}
        <div
          className="vault-selector-card"
          data-testid="selector-card-trading"
          onClick={() => handleSelect("trading")}
          onMouseEnter={() => setHoverTrading(true)}
          onMouseLeave={() => setHoverTrading(false)}
          style={{
            background: C.cardBg,
            border: `1px solid ${hoverTrading ? C.green : C.cardBorder}`,
            borderRadius: 12,
            cursor: "pointer",
            transition: "border-color 0.2s, transform 0.2s",
            transform: hoverTrading ? "translateY(-4px)" : "translateY(0)",
            padding: "32px 28px",
            width: 320,
            minHeight: 380,
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          {/* LIVE pill — top right */}
          <div
            data-testid="selector-live-badge"
            style={{
              position: "absolute",
              top: 20,
              right: 20,
              background: "rgba(16,185,129,0.12)",
              color: C.green,
              fontSize: 10,
              fontWeight: 600,
              padding: "3px 10px",
              borderRadius: 20,
              border: "1px solid rgba(16,185,129,0.25)",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              letterSpacing: "0.04em",
            }}
          >
            <span
              data-testid="selector-live-dot"
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: C.green,
                display: "inline-block",
                animation: "vault_app_selector_pulse 2s infinite",
              }}
            />
            LIVE
          </div>

          {/* Icon */}
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 10,
              background: "rgba(16,185,129,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="4"    y="6" width="3" height="8" rx="1" fill={C.green} />
              <line x1="5.5" y1="4"  x2="5.5" y2="6"  stroke={C.green} strokeWidth="1.5" />
              <line x1="5.5" y1="14" x2="5.5" y2="16" stroke={C.green} strokeWidth="1.5" />
              <rect x="10.5" y="9"  width="3" height="6" rx="1" fill="#ef4444" />
              <line x1="12"  y1="7"  x2="12"  y2="9"  stroke="#ef4444" strokeWidth="1.5" />
              <line x1="12"  y1="15" x2="12"  y2="17" stroke="#ef4444" strokeWidth="1.5" />
              <rect x="17"   y="5"  width="3" height="9" rx="1" fill={C.green} />
              <line x1="18.5" y1="3"  x2="18.5" y2="5"  stroke={C.green} strokeWidth="1.5" />
              <line x1="18.5" y1="14" x2="18.5" y2="16" stroke={C.green} strokeWidth="1.5" />
            </svg>
          </div>

          {/* App name */}
          <div
            style={{
              marginTop: 20,
              color: C.cardText,
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: "-0.02em",
            }}
          >
            THE TRADING VAULT
          </div>
          <div style={{ color: C.cardMuted, fontSize: 13, marginTop: 4 }}>
            Pro Trading Suite
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: C.cardBorder, margin: "20px 0" }} />

          {/* Feature list */}
          <ul style={{ listStyle: "none", margin: 0, padding: 0, color: C.cardMuted, fontSize: 12, lineHeight: 1.8 }}>
            {[
              "Live prices · Scanner · Options chain",
              "Earnings Edge · AI Analysis · Macro",
              "Trade Plan · Journal · Risk Tools",
            ].map((f) => (
              <li key={f} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ color: C.green, fontWeight: 700 }}>•</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>

          {/* Bottom button */}
          <button
            data-testid="selector-btn-trading"
            onClick={(e) => { e.stopPropagation(); handleSelect("trading"); }}
            onMouseEnter={() => setHoverBtnTrading(true)}
            onMouseLeave={() => setHoverBtnTrading(false)}
            style={{
              marginTop: "auto",
              background: hoverBtnTrading ? C.greenHov : C.green,
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "12px 20px",
              fontSize: 13,
              fontWeight: 600,
              width: "100%",
              cursor: "pointer",
              transition: "background 0.15s",
            }}
          >
            Open Trading Vault →
          </button>
        </div>
      </div>

      {/* ── Remember choice ─────────────────────────────── */}
      <label
        data-testid="selector-remember-label"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 28,
          color: C.cardMuted,
          fontSize: 11,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <input
          type="checkbox"
          data-testid="selector-remember-checkbox"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
          style={{ width: 16, height: 16, cursor: "pointer", accentColor: C.indigo }}
        />
        Remember my choice and skip this screen next time
      </label>

      {/* ── Sign out ─────────────────────────────────────── */}
      <button
        data-testid="selector-signout-btn"
        onClick={onLogout}
        style={{
          marginTop: "auto",
          paddingTop: 40,
          color: C.link,
          fontSize: 11,
          background: "none",
          border: "none",
          cursor: "pointer",
          fontFamily: SYSTEM_FONT,
        }}
      >
        Sign out
      </button>
    </div>
  );
}
